import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Music2, Link as LinkIcon, Mail, User, CheckCircle, ChevronRight } from "lucide-react";
import { GENRE_HIERARCHY } from "@/constants/genres";

// Helper to get pending applications from localStorage
export const getCuratorApplications = () => {
  try {
    return JSON.parse(localStorage.getItem('curator_applications') || '[]');
  } catch {
    return [];
  }
};

// Helper to save applications to localStorage
export const saveCuratorApplications = (applications) => {
  localStorage.setItem('curator_applications', JSON.stringify(applications));
};

function CuratorSubmit() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    playlist_name: "",
    playlist_url: "",
    contact_email: "",
    contact_name: "",
    genre: "",
    additional_info: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.playlist_name || !formData.playlist_url || !formData.contact_email || !formData.contact_name || !formData.genre) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Basic URL validation
    try {
      new URL(formData.playlist_url);
    } catch {
      toast.error("Please enter a valid playlist URL");
      return;
    }

    setLoading(true);

    try {
      // Create application object
      const application = {
        id: crypto.randomUUID(),
        ...formData,
        status: 'pending', // pending, approved, rejected
        rate_cents: null, // Will be set by admin (100-400 = $1-$4)
        created_at: new Date().toISOString(),
        reviewed_at: null,
        admin_notes: null
      };

      // Save to localStorage for admin review
      const applications = getCuratorApplications();
      applications.push(application);
      saveCuratorApplications(applications);

      // Also send email notification to admin
      try {
        await fetch(
          `https://apefafnhhsjmkstfwteh.supabase.co/functions/v1/curator-playlist-submission`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(formData),
          }
        );
      } catch (emailErr) {
        console.error("Email notification failed:", emailErr);
        // Don't fail the submission if email fails
      }

      setSubmitted(true);
      toast.success("Application submitted! We'll review it shortly.");
    } catch (err) {
      console.error("Submission error:", err);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-lg bg-[#121212] text-[#e0e0e0] border border-[#1f1f1f]">
          <CardContent className="pt-8 pb-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Application Submitted!</h2>
            <p className="text-[#a0a0a0] mb-6">
              Thank you for applying to become a curator. We'll review your playlist and get back to you soon.
            </p>
            <Button
              onClick={() => navigate("/auth")}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Back to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <Card className="w-full max-w-lg bg-[#121212] text-[#e0e0e0] border border-[#1f1f1f]">
        <CardHeader>
          <button
            onClick={() => navigate("/auth")}
            className="flex items-center gap-2 text-sm text-[#808080] hover:text-[#e0e0e0] mb-4 transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Sign In
          </button>
          <CardTitle className="text-2xl flex items-center gap-3">
            <Music2 className="text-purple-500" />
            Curator Playlist Submission
          </CardTitle>
          <CardDescription className="text-[#a0a0a0]">
            Submit your playlist to become a curator on Soundope. We'll review and set your rate ($1-$4 per submission).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <label className="text-sm text-[#a0a0a0] flex items-center gap-2 mb-1.5">
                <User size={14} />
                Your Name *
              </label>
              <Input
                type="text"
                value={formData.contact_name}
                onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                placeholder="John Doe"
                required
                className="bg-[#0f0f0f] border-[#1f1f1f] text-[#e0e0e0]"
              />
            </div>

            <div>
              <label className="text-sm text-[#a0a0a0] flex items-center gap-2 mb-1.5">
                <Mail size={14} />
                Contact Email *
              </label>
              <Input
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                placeholder="you@email.com"
                required
                className="bg-[#0f0f0f] border-[#1f1f1f] text-[#e0e0e0]"
              />
            </div>

            <div>
              <label className="text-sm text-[#a0a0a0] flex items-center gap-2 mb-1.5">
                <Music2 size={14} />
                Playlist Name *
              </label>
              <Input
                type="text"
                value={formData.playlist_name}
                onChange={(e) => setFormData(prev => ({ ...prev, playlist_name: e.target.value }))}
                placeholder="e.g. Dark Trap Essentials, Underground Horrorcore"
                required
                className="bg-[#0f0f0f] border-[#1f1f1f] text-[#e0e0e0]"
              />
            </div>

            <div>
              <label className="text-sm text-[#a0a0a0] flex items-center gap-2 mb-1.5">
                <LinkIcon size={14} />
                Playlist URL *
              </label>
              <Input
                type="url"
                value={formData.playlist_url}
                onChange={(e) => setFormData(prev => ({ ...prev, playlist_url: e.target.value }))}
                placeholder="https://open.spotify.com/playlist/..."
                required
                className="bg-[#0f0f0f] border-[#1f1f1f] text-[#e0e0e0]"
              />
              <p className="text-xs text-[#606060] mt-1">Spotify, Apple Music, SoundCloud, etc.</p>
            </div>

            <div>
              <label className="text-sm text-[#a0a0a0] flex items-center gap-2 mb-1.5">
                <Music2 size={14} />
                Genre / Style *
              </label>
              <Select
                value={formData.genre}
                onValueChange={(value) => setFormData(prev => ({ ...prev, genre: value }))}
              >
                <SelectTrigger className="bg-[#0f0f0f] border-[#1f1f1f] text-[#e0e0e0] hover:border-purple-500/50 transition-colors">
                  <SelectValue placeholder="Select genre..." />
                </SelectTrigger>
                <SelectContent className="bg-[#0f0f0f] border border-[#2a2a2a] max-h-[350px] z-50 overflow-hidden">
                  {Object.entries(GENRE_HIERARCHY).map(([parentGenre, subgenres]) => (
                    <SelectGroup key={parentGenre}>
                      <SelectLabel className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-purple-400 bg-[#1a1a1a] sticky top-0">
                        {parentGenre}
                      </SelectLabel>
                      <SelectItem 
                        value={parentGenre} 
                        className="text-[#e0e0e0] pl-4 py-2 cursor-pointer hover:bg-purple-500/20 focus:bg-purple-500/20 focus:text-white"
                      >
                        <span className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                          All {parentGenre}
                        </span>
                      </SelectItem>
                      {subgenres.map((subgenre) => (
                        <SelectItem 
                          key={subgenre} 
                          value={subgenre}
                          className="text-[#b0b0b0] pl-6 py-1.5 cursor-pointer hover:bg-purple-500/10 hover:text-white focus:bg-purple-500/10 focus:text-white"
                        >
                          <span className="flex items-center gap-2">
                            <ChevronRight size={12} className="text-[#505050]" />
                            {subgenre}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm text-[#a0a0a0] mb-1.5 block">
                Additional Info (optional)
              </label>
              <Textarea
                value={formData.additional_info}
                onChange={(e) => setFormData(prev => ({ ...prev, additional_info: e.target.value }))}
                placeholder="Tell us about your playlist, follower count, or anything else..."
                className="bg-[#0f0f0f] border-[#1f1f1f] text-[#e0e0e0] min-h-[80px]"
              />
            </div>

            <Button type="submit" disabled={loading} className="w-full bg-purple-600 hover:bg-purple-700">
              {loading ? "Submitting..." : "Submit Playlist for Review"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default CuratorSubmit;
